import { clientContacts, jobContactDuties, jobContactTags, positionIsRoleWord, siteContacts } from '@fieldops/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { ContactFields, type ClientPerson } from '../client/ContactList.js'
import { EmailCompose } from '../email/EmailCompose.js'
import { Icon } from '../Icon.js'
import { Badge, Button, EmptyState, Input } from '../ui.js'
import { emailContacts } from './panes.js'
import { SmsCompose } from './SmsCompose.js'
import type { Job, JobContact } from './types.js'

type OfferedRole = 'JOB' | 'SITE' | 'OTHER'
type ClientContact = Job['client']['contacts'][number]

type UpdateContact = (input: {
  id: string
  roles?: OfferedRole[]
  label?: string | null
  receivesComms?: boolean
}) => void

/**
 * The people on this job, each of them once, as a card apiece.
 *
 * A person wears roles — Job, the one we deal with about the work, and Site,
 * who we meet at the property — plus an optional name of our own. Billing sits
 * in the same switch but is one person, so pressing it on somebody moves it
 * off whoever had it. Gets comms is who hears about the job besides that, and
 * lives behind Edit with the person's own details. With exactly one person
 * none of this needs pressing: they are all of it. Decision 84.
 *
 * Somebody new is typed into the box under the cards, through the client
 * screen's own fields, and lands on the client as well as the job. The server
 * refuses somebody already on the client by email or mobile, so the same
 * person is not typed in twice.
 */
export function ContactsPane({
  job,
  canEdit,
  isSaving,
  onAddContact,
  onUpdateContact,
  onSetBilling,
  onRemoveContact,
}: {
  job: Job
  canEdit: boolean
  isSaving: boolean
  onAddContact: (contactId: string) => void
  onUpdateContact: UpdateContact
  onSetBilling: (jobContactId: string) => void
  onRemoveContact: (id: string) => void
}) {
  const headingId = useId()
  // `jobs.createContact` and `jobs.saveContact` are `JOB_EDIT`: a person is
  // added and fixed through the job, not by editing the client at large.
  const canCreate = canEdit
  const [adding, setAdding] = useState<{ firstName: string; lastName: string } | null>(null)

  const duties = jobContactDuties(job.contacts)
  const fromClient = job.contacts.length === 0
  const people = job.client.contacts
  const ofClient = clientContacts(people)
  const atThisSite = job.site ? siteContacts(people, job.site.id) : []
  const onJob = (contact: ClientContact) => job.contacts.some((link) => link.contact.id === contact.id)

  const offers = [
    ...(fromClient ? [] : ofClient.filter((contact) => !onJob(contact))).map((contact) => ({
      contact,
      atSite: false,
    })),
    ...atThisSite.filter((contact) => !onJob(contact)).map((contact) => ({ contact, atSite: true })),
  ]

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h2 id={headingId} className="text-[14.5px] font-semibold text-ink-900">
          People
        </h2>
        <span className="text-[12.5px] text-ink-500">
          {fromClient
            ? 'From the client record — nobody is named on this job yet'
            : `${job.contacts.length} on this job`}
        </span>
        {canCreate && !adding ? (
          <button
            type="button"
            onClick={() => setAdding({ firstName: '', lastName: '' })}
            className="ml-auto inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-600 hover:text-brand-700"
          >
            <Icon name="plus" className="size-3.5" />
            Add person
          </button>
        ) : null}
      </div>

      {fromClient ? (
        ofClient.length === 0 ? (
          <div className="rounded-[11px] bg-white ring-1 ring-ink-200 ring-inset">
            <EmptyState
              title="No contact on the client"
              description={
                atThisSite.length > 0
                  ? 'This property has its own contacts below. Most jobs do not need one.'
                  : canCreate
                    ? 'Add a new person below.'
                    : 'Add a person on the client record.'
              }
            />
          </div>
        ) : (
          <ul aria-label="The client’s contacts" className="flex flex-col gap-2.5">
            {ofClient.map((contact) => (
              <ClientPersonCard
                key={contact.id}
                job={job}
                contact={contact}
                canEdit={canEdit}
                isSaving={isSaving}
                onAddContact={onAddContact}
              />
            ))}
          </ul>
        )
      ) : (
        <ul aria-label="People on this job" className="flex flex-col gap-2.5">
          {job.contacts.map((link) => (
            <JobPersonCard
              key={link.id}
              job={job}
              link={link}
              sole={duties.sole}
              billing={duties.billing?.id === link.id}
              canEdit={canEdit}
              isSaving={isSaving}
              onUpdateContact={onUpdateContact}
              onSetBilling={onSetBilling}
              onRemoveContact={onRemoveContact}
            />
          ))}
        </ul>
      )}

      {!fromClient && !duties.billing && job.contacts.length > 1 ? (
        <p className="text-xs text-ink-500">Choose who the invoices go to.</p>
      ) : null}

      {adding ? (
        <NewPerson job={job} start={adding} onClose={() => setAdding(null)} />
      ) : canEdit && (offers.length > 0 || canCreate) ? (
        <AddBox
          offers={offers}
          canCreate={canCreate}
          isSaving={isSaving}
          onAddContact={onAddContact}
          onNewPerson={(typed) => setAdding(splitName(typed))}
        />
      ) : null}
    </section>
  )
}

/**
 * Somebody the client record does not have yet.
 *
 * They belong to the client unless "Only at this property" is ticked, which
 * files them under the job's site (decision 85). Either way they come onto
 * this job as its contact.
 */
function NewPerson({
  job,
  start,
  onClose,
}: {
  job: Job
  start: { firstName: string; lastName: string }
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [atSite, setAtSite] = useState(false)

  const create = useMutation(
    trpc.jobs.createContact.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        void queryClient.invalidateQueries(trpc.clients.pathFilter())
        void queryClient.invalidateQueries(trpc.schedule.pathFilter())
        onClose()
      },
    }),
  )

  return (
    <div className="rounded-[11px] bg-white p-4 ring-1 ring-ink-200 ring-inset">
      <ContactFields
        idPrefix={`job-${job.id}`}
        person={start.firstName ? draftPerson(start) : null}
        submitLabel="Add to job"
        isPending={create.isPending}
        error={create.error ? errorMessage(create.error) : null}
        onCancel={onClose}
        onSubmit={(contact) =>
          create.mutate({
            jobId: job.id,
            contact,
            atSite,
            roles: atSite ? ['SITE'] : ['JOB'],
          })
        }
      >
        {job.site ? (
          <label className="flex items-center gap-2 text-xs text-ink-700">
            <input
              type="checkbox"
              checked={atSite}
              onChange={(event) => setAtSite(event.target.checked)}
              className="size-3.5 rounded border-ink-300 text-ink-900 focus:ring-brand-600"
            />
            Only at this property — a tenant or site manager, not somebody at the client
          </label>
        ) : null}
      </ContactFields>
    </div>
  )
}

/**
 * The dashed box under the cards: find somebody already on the client, or
 * start a new person from the name typed.
 *
 * The client's own people come first and the property's after them, marked,
 * because most jobs need only the client's (decision 85).
 */
function AddBox({
  offers,
  canCreate,
  isSaving,
  onAddContact,
  onNewPerson,
}: {
  offers: Array<{ contact: ClientContact; atSite: boolean }>
  canCreate: boolean
  isSaving: boolean
  onAddContact: (contactId: string) => void
  onNewPerson: (typed: string) => void
}) {
  const [term, setTerm] = useState('')
  const query = term.trim().toLowerCase()
  const matches = query
    ? offers.filter(({ contact }) => searchText(contact).includes(query)).slice(0, 6)
    : []

  const add = (contactId: string) => {
    setTerm('')
    onAddContact(contactId)
  }

  return (
    <div
      role="group"
      aria-label="Add someone to this job"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[11px] border border-dashed border-ink-300 px-3.5 py-3"
    >
      <div className="relative min-w-[220px] flex-1">
        <Input
          aria-label="Find or add a person"
          value={term}
          placeholder={canCreate ? 'Search client contacts or type a new name…' : 'Search client contacts…'}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setTerm('')
            if (event.key !== 'Enter' || !query) return
            event.preventDefault()
            const first = matches[0]
            if (first) add(first.contact.id)
            else if (canCreate) onNewPerson(term.trim())
          }}
        />
        {query ? (
          <ul className="absolute inset-x-0 top-full z-10 mt-1 flex flex-col overflow-hidden rounded-lg bg-white py-1 shadow-lg ring-1 ring-ink-200">
            {matches.map(({ contact, atSite }) => (
              <li key={contact.id}>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => add(contact.id)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-ink-50"
                >
                  <span className="min-w-0 truncate font-medium text-ink-900">{contactName(contact)}</span>
                  <span className="min-w-0 truncate text-xs text-ink-500">
                    {[atSite ? 'At this property' : null, contactReach(contact)]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </button>
              </li>
            ))}
            {canCreate ? (
              <li>
                <button
                  type="button"
                  onClick={() => onNewPerson(term.trim())}
                  className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[13px] font-semibold text-brand-600 hover:bg-ink-50"
                >
                  <Icon name="plus" className="size-3.5" />
                  Add “{term.trim()}” as a new person
                </button>
              </li>
            ) : matches.length === 0 ? (
              <li className="px-3 py-1.5 text-xs text-ink-500">Nobody on the client by that name.</li>
            ) : null}
          </ul>
        ) : null}
      </div>

      {offers.length > 0 ? (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[12.5px]">
          <span className="text-ink-500">Suggested:</span>
          {offers.slice(0, 3).map(({ contact, atSite }) => (
            <button
              key={contact.id}
              type="button"
              disabled={isSaving}
              onClick={() => add(contact.id)}
              className="inline-flex items-baseline gap-1 font-semibold text-brand-600 hover:text-brand-700 disabled:opacity-50"
            >
              <span>{contactName(contact)}</span>
              {atSite ? <span className="text-[11px] font-medium text-ink-400">site</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** A person on the job, with what they are here and how to reach them. */
function JobPersonCard({
  job,
  link,
  sole,
  billing,
  canEdit,
  isSaving,
  onUpdateContact,
  onSetBilling,
  onRemoveContact,
}: {
  job: Job
  link: JobContact
  sole: boolean
  billing: boolean
  canEdit: boolean
  isSaving: boolean
  onUpdateContact: UpdateContact
  onSetBilling: (jobContactId: string) => void
  onRemoveContact: (id: string) => void
}) {
  const canEditPerson = canEdit
  const [editing, setEditing] = useState(false)
  const [focusCustom, setFocusCustom] = useState(false)

  const contact = link.contact
  const name = contactName(contact)
  const optedOut = contact.optedOutOfNotifications
  const roles = offered(link.roles)

  const setRoles = (next: OfferedRole[], label?: string | null) =>
    onUpdateContact({ id: link.id, roles: next, ...(label !== undefined ? { label } : {}) })

  const toggle = (role: 'JOB' | 'SITE') => {
    if (role === 'JOB' && sole) return
    const without = roles.filter((item) => item !== role)
    setRoles(roles.includes(role) ? without : [...without, role])
  }

  const duties = [
    ...jobContactTags(link, sole).map((tag) =>
      tag === 'Job' ? 'Job contact' : tag === 'Site' ? 'Site contact' : tag,
    ),
    optedOut ? 'Opted out of comms' : link.receivesComms ? 'Gets comms' : null,
  ]

  return (
    <PersonCard
      name={name}
      line={[jobTitle(contact), ...duties].filter(Boolean).join(' · ')}
      action={
        canEdit ? (
          <button
            type="button"
            aria-expanded={editing}
            aria-label={`${editing ? 'Done editing' : 'Edit'} ${name}`}
            onClick={() => setEditing((open) => !open)}
            className="text-[12.5px] font-medium text-ink-500 hover:text-ink-900"
          >
            {editing ? 'Done' : 'Edit'}
          </button>
        ) : null
      }
    >
      {editing && canEditPerson ? (
        <EditPerson job={job} contact={contact} onDone={() => setEditing(false)} />
      ) : (
        <ReachTiles job={job} contact={contact} onAddDetail={canEditPerson ? () => setEditing(true) : undefined} />
      )}

      {canEdit ? (
        <div
          role="group"
          aria-label={`What ${name} is on this job`}
          className="inline-flex flex-wrap items-center gap-0.5 self-start rounded-lg bg-ink-100 p-0.5"
        >
          <Segment pressed={sole || roles.includes('JOB')} disabled={sole || isSaving} onClick={() => toggle('JOB')}>
            Job
          </Segment>
          <Segment pressed={roles.includes('SITE')} disabled={isSaving} onClick={() => toggle('SITE')}>
            Site
          </Segment>
          <Segment
            pressed={billing}
            disabled={sole || billing || isSaving}
            onClick={() => onSetBilling(link.id)}
          >
            Billing
          </Segment>
          {roles.includes('OTHER') ? (
            <input
              aria-label={`Custom role for ${name}`}
              defaultValue={link.label ?? ''}
              placeholder="What they are"
              autoFocus={focusCustom}
              onBlur={(event) => {
                setFocusCustom(false)
                const label = event.target.value.trim()
                if (label === (link.label ?? '')) return
                if (label) setRoles(roles, label)
                else setRoles(roles.filter((role) => role !== 'OTHER'), null)
              }}
              className="h-7 w-32 rounded-md bg-white px-2.5 text-xs font-semibold text-ink-900 shadow-sm ring-1 ring-ink-200 ring-inset"
            />
          ) : (
            <Segment
              pressed={false}
              disabled={isSaving}
              onClick={() => {
                setFocusCustom(true)
                setRoles([...roles, 'OTHER'], null)
              }}
            >
              + Custom
            </Segment>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {jobContactTags(link, sole).map((tag) => (
            <Badge key={tag} tone={tag === 'Billing' ? 'green' : 'neutral'}>
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {editing ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-ink-100 pt-3">
          <Choice
            label={optedOut ? 'Opted out of comms' : 'Gets comms about this job'}
            aria-label={`Gets comms for ${name}`}
            checked={link.receivesComms && !optedOut}
            disabled={optedOut || isSaving}
            onChange={(event) => onUpdateContact({ id: link.id, receivesComms: event.target.checked })}
          />
          <button
            type="button"
            className="ml-auto text-xs font-semibold text-ink-500 hover:text-red-700"
            onClick={() => onRemoveContact(link.id)}
          >
            Remove from job
          </button>
        </div>
      ) : null}
    </PersonCard>
  )
}

/**
 * A client contact on a job nobody has been named on yet. They are who the
 * office rings, so they can be reached from here; Add to job names them.
 */
function ClientPersonCard({
  job,
  contact,
  canEdit,
  isSaving,
  onAddContact,
}: {
  job: Job
  contact: ClientContact
  canEdit: boolean
  isSaving: boolean
  onAddContact: (contactId: string) => void
}) {
  return (
    <PersonCard
      name={contactName(contact)}
      badge={contact.isPrimary ? <Badge tone="green">Primary</Badge> : null}
      line={jobTitle(contact) ?? 'Client contact'}
      action={
        canEdit ? (
          <Button size="sm" loading={isSaving} onClick={() => onAddContact(contact.id)}>
            Add to job
          </Button>
        ) : null
      }
    >
      <ReachTiles job={job} contact={contact} />
    </PersonCard>
  )
}

function PersonCard({
  name,
  badge,
  line,
  action,
  children,
}: {
  name: string
  badge?: ReactNode
  line: string
  action: ReactNode
  children: ReactNode
}) {
  return (
    <li
      aria-label={name}
      className="flex flex-col gap-3 rounded-[11px] bg-white p-4 ring-1 ring-ink-200 ring-inset"
    >
      <div className="flex items-center gap-3">
        <Initials name={name} />
        <div className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[14.5px] font-semibold text-ink-900">{name}</span>
            {badge}
          </span>
          <span className="truncate text-[12.5px] text-ink-500">{line}</span>
        </div>
        {action ? <div className="ml-auto shrink-0">{action}</div> : null}
      </div>
      {children}
    </li>
  )
}

/**
 * Phone and email as two tiles, each with what can be done with it.
 *
 * Call hands off to the phone; SMS and Email open their compose windows. SMS
 * is offered only against a mobile. A detail not on file is a dashed tile,
 * which offers to add it to anybody who can edit the person.
 */
function ReachTiles({
  job,
  contact,
  onAddDetail,
}: {
  job: Job
  contact: ClientContact
  onAddDetail?: (() => void) | undefined
}) {
  const [open, setOpen] = useState<'sms' | 'email' | null>(null)
  const name = contactName(contact)
  const number = contact.mobile ?? contact.phone
  const dialable = number?.replace(/[^\d+]/g, '') ?? ''

  return (
    <div className="grid gap-2.5 sm:grid-cols-2">
      {number ? (
        <Tile label={contact.mobile ? 'Mobile' : 'Phone'} value={number}>
          {dialable ? <TileAction href={`tel:${dialable}`}>Call</TileAction> : null}
          {contact.mobile ? <TileAction onClick={() => setOpen('sms')}>SMS</TileAction> : null}
        </Tile>
      ) : (
        <MissingTile label="Phone" onAdd={onAddDetail} />
      )}

      {contact.email ? (
        <Tile label="Email" value={contact.email}>
          <TileAction onClick={() => setOpen('email')}>Email</TileAction>
        </Tile>
      ) : (
        <MissingTile label="Email" onAdd={onAddDetail} />
      )}

      <SmsCompose
        open={open === 'sms'}
        onClose={() => setOpen(null)}
        contactName={name}
        phone={contact.mobile}
        jobId={job.id}
      />
      <EmailCompose
        open={open === 'email'}
        onClose={() => setOpen(null)}
        jobId={job.id}
        clientId={job.client.id}
        contacts={emailContacts(job)}
        files={job.attachments.map((file) => ({ id: file.id, name: file.filename }))}
        initial={{ to: contact.email ? [contact.email] : [], subject: '', body: '' }}
      />
    </div>
  )
}

function Tile({ label, value, children }: { label: string; value: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-lg border border-ink-200 px-3 py-2">
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-[11px] text-ink-500">{label}</span>
        <span className="truncate text-[13px] font-medium text-ink-900">{value}</span>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">{children}</div>
    </div>
  )
}

function MissingTile({ label, onAdd }: { label: string; onAdd?: (() => void) | undefined }) {
  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-dashed border-ink-300 px-3 py-2">
      <span className="text-[11px] text-ink-500">{label}</span>
      {onAdd ? (
        <button
          type="button"
          onClick={onAdd}
          className="self-start text-[13px] font-semibold text-brand-600 hover:text-brand-700"
        >
          + Add {label.toLowerCase()}
        </button>
      ) : (
        <span className="text-[13px] text-ink-400">Not on file</span>
      )}
    </div>
  )
}

function TileAction({
  href,
  onClick,
  children,
}: {
  href?: string
  onClick?: () => void
  children: string
}) {
  const className =
    'inline-flex items-center rounded-md bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-100'

  return href ? (
    <a href={href} className={className}>
      {children}
    </a>
  ) : (
    <button type="button" onClick={onClick} className={className}>
      {children}
    </button>
  )
}

/** The person's own details, saved through the job to the client record they belong to. */
function EditPerson({
  job,
  contact,
  onDone,
}: {
  job: Job
  contact: JobContact['contact']
  onDone: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const save = useMutation(
    trpc.jobs.saveContact.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        void queryClient.invalidateQueries(trpc.clients.pathFilter())
        onDone()
      },
    }),
  )

  return (
    <ContactFields
      idPrefix={`edit-${contact.id}`}
      person={contact}
      submitLabel="Save"
      isPending={save.isPending}
      error={save.error ? errorMessage(save.error) : null}
      onCancel={onDone}
      onSubmit={(values) => save.mutate({ jobId: job.id, contactId: contact.id, ...values })}
    />
  )
}

/** One of the role switch's buttons. Several can be pressed at once. */
function Segment({
  pressed,
  disabled,
  onClick,
  children,
}: {
  pressed: boolean
  disabled?: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1 text-xs font-semibold transition-colors',
        pressed ? 'bg-white text-ink-900 shadow-sm ring-1 ring-ink-200 ring-inset' : 'text-ink-500',
        !pressed && !disabled && 'hover:text-ink-800',
        disabled && 'cursor-default',
      )}
    >
      {children}
    </button>
  )
}

/**
 * Gets comms, as the app's own tick — ink-900 when on, a hairline ring when
 * off — rather than the browser's blue control. Ink means "this is on".
 */
function Choice({
  label,
  disabled,
  checked,
  ...input
}: {
  label: string
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'>) {
  const on = checked === true

  return (
    <label
      className={cn(
        'inline-flex items-center gap-1.5 text-xs font-semibold select-none',
        disabled ? 'cursor-default' : 'cursor-pointer',
        disabled && !on ? 'text-ink-400' : 'text-ink-700',
      )}
    >
      <span className={cn('inline-grid size-4 shrink-0 place-items-center', disabled && 'opacity-80')}>
        <input
          type="checkbox"
          disabled={disabled}
          checked={checked}
          className="col-start-1 row-start-1 size-4 cursor-pointer appearance-none bg-transparent disabled:cursor-default"
          {...input}
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none col-start-1 row-start-1 size-4 rounded-[4px] ring-1 ring-inset',
            on ? 'bg-brand-800 ring-brand-800' : 'bg-white ring-ink-300',
          )}
        />
        {on ? (
          <Icon
            name="check"
            strokeWidth={3}
            className="pointer-events-none col-start-1 row-start-1 size-2.5 text-white"
          />
        ) : null}
      </span>
      {label}
    </label>
  )
}

const INITIALS_TONES = [
  'bg-brand-50 text-brand-700',
  'bg-indigo-50 text-indigo-700',
  'bg-sky-50 text-sky-700',
  'bg-amber-50 text-amber-800',
]

/** A soft initials disc. The tone is picked from the name, so it is steady between visits. */
function Initials({ name }: { name: string }) {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
  const hash = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0)

  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid size-10 shrink-0 place-items-center rounded-full text-[13px] font-semibold',
        INITIALS_TONES[hash % INITIALS_TONES.length],
      )}
    >
      {letters || '?'}
    </span>
  )
}

function offered(roles: readonly string[]): OfferedRole[] {
  return roles.filter((role): role is OfferedRole => role === 'JOB' || role === 'SITE' || role === 'OTHER')
}

type Named = {
  firstName: string
  lastName: string | null
  position: string | null
  phone: string | null
  mobile: string | null
  email: string | null
}

function contactName(contact: Named): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ')
}

/** A job title. ServiceM8's type word ("JOB") is a role now, so it is not repeated here. */
function jobTitle(contact: Named): string | null {
  return positionIsRoleWord(contact.position) ? null : contact.position
}

function contactReach(contact: Named): string | null {
  return contact.mobile ?? contact.phone ?? contact.email
}

function searchText(contact: Named): string {
  return [contactName(contact), contact.position, contact.email, contact.mobile, contact.phone]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function splitName(typed: string): { firstName: string; lastName: string } {
  const [firstName = '', ...rest] = typed.split(/\s+/).filter(Boolean)
  return { firstName, lastName: rest.join(' ') }
}

/** A name typed into the search box, as the starting values of the new-person form. */
function draftPerson(start: { firstName: string; lastName: string }): ClientPerson {
  return {
    id: '',
    firstName: start.firstName,
    lastName: start.lastName || null,
    position: null,
    email: null,
    phone: null,
    mobile: null,
    isPrimary: false,
    siteId: null,
  }
}
